import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import authService from '../../services/authService';
import { useAuth } from '../../context/AuthContext';
import bookingsService from '../../services/bookingsService';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Badge,
  Container,
  CircularProgress,
  alpha,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  BookOnline as BookingsIcon,
  Favorite as FavoritesIcon,
  EditNote as EditNoteIcon,
  Send as RequestsIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  AccountCircle,
  Home as HomeIcon,
  Explore as ExploreIcon
} from '@mui/icons-material';
import logo from '../../logos/logo-transparent.png';

const drawerWidth = 280;

const UserLayout = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [user, setUser] = useState(null);
  const [bookingsCount, setBookingsCount] = useState(0);

  const unreadNotifications = 0; // TODO: from API when available

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotifOpen = (event) => {
    setNotifAnchor(event.currentTarget);
  };

  const handleNotifClose = () => {
    setNotifAnchor(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Verify auth before showing user dashboard - redirect to login if not authenticated
  useEffect(() => {
    const checkAuth = async () => {
      if (!authService.isAuthenticated()) {
        navigate('/login', { replace: true });
        return;
      }
      try {
        await authService.getCurrentUser();
        setUser(authService.getStoredUser());
      } catch {
        authService.logout();
        navigate('/login', { replace: true });
        return;
      }
      setAuthReady(true);
    };
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    if (!authReady) return;
    const fetchBookingsCount = async () => {
      try {
        const res = await bookingsService.getMyBookings();
        const bookings = res?.bookings || [];
        setBookingsCount(bookings.length);
      } catch {
        setBookingsCount(0);
      }
    };
    fetchBookingsCount();
  }, [authReady]);

  // Refresh user when profile is updated (e.g. profile picture upload)
  useEffect(() => {
    const handleProfileUpdated = () => setUser(authService.getStoredUser());
    window.addEventListener('profile-updated', handleProfileUpdated);
    return () => window.removeEventListener('profile-updated', handleProfileUpdated);
  }, []);

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/user/dashboard' },
    { text: 'My Profile', icon: <PersonIcon />, path: '/user/profile' },
    { text: 'My Bookings', icon: <BookingsIcon />, path: '/user/bookings', badge: bookingsCount },
    { text: 'My Plans', icon: <EditNoteIcon />, path: '/user/plans' },
    { text: 'My Requests', icon: <RequestsIcon />, path: '/user/requests' },
    { text: 'Favorites', icon: <FavoritesIcon />, path: '/user/favorites' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/user/settings' },
  ];

  const SIDEBAR_BG = '#1c1206';
  const MUTED = alpha('#ffffff', 0.68);
  const go = (path) => { navigate(path); if (isMobile) setMobileOpen(false); };
  const initials = user
    ? (((user.first_name || user.firstName || '').charAt(0) + (user.last_name || user.lastName || '').charAt(0)).trim() || (user.email || '?').charAt(0).toUpperCase())
    : '?';
  const displayName = user ? `${user.first_name || user.firstName || ''} ${user.last_name || user.lastName || ''}`.trim() || user.email : 'User';

  const navButton = (item) => {
    const isActive = location.pathname === item.path;
    return (
      <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
        <ListItemButton
          onClick={() => go(item.path)}
          sx={{
            borderRadius: 2.5, px: 1.5, py: 1,
            color: isActive ? '#fff' : MUTED,
            bgcolor: isActive ? theme.palette.primary.main : 'transparent',
            boxShadow: isActive ? `0 8px 20px ${alpha(theme.palette.primary.main, 0.45)}` : 'none',
            '&:hover': { bgcolor: isActive ? theme.palette.primary.dark : alpha('#fff', 0.07), color: '#fff' },
            transition: 'all 0.2s ease',
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 38 }}>
            {item.badge != null && item.badge > 0 ? (
              <Badge badgeContent={item.badge} color="error">{item.icon}</Badge>
            ) : item.icon}
          </ListItemIcon>
          <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: isActive ? 700 : 500, fontSize: '0.9rem' }} />
        </ListItemButton>
      </ListItem>
    );
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: SIDEBAR_BG, color: '#fff' }}>
      {/* Brand */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 2.5, cursor: 'pointer' }} onClick={() => navigate('/')}>
        <Box component="img" src={logo} alt="Royal Dastinos" sx={{ height: 36, filter: 'brightness(1.1)' }} />
        <Typography sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, color: '#fff', fontSize: '1.1rem' }}>
          Royal Dastinos
        </Typography>
      </Box>

      {/* Profile card */}
      <Box sx={{ mx: 2, mb: 1.5, p: 1.75, borderRadius: 3, bgcolor: alpha('#fff', 0.06), border: `1px solid ${alpha('#fff', 0.09)}`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar
          src={user?.profile_picture_url ?? user?.profile_picture ?? user?.profilePicture}
          sx={{ width: 46, height: 46, bgcolor: theme.palette.primary.main, fontWeight: 700, fontSize: '1.05rem' }}
        >
          {initials}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '0.92rem' }} noWrap>{displayName}</Typography>
          <Typography sx={{ color: alpha('#fff', 0.5), fontSize: '0.72rem' }} noWrap>{user?.email || ''}</Typography>
        </Box>
      </Box>

      {/* Nav */}
      <List sx={{ px: 1.5, py: 0.5, flex: 1 }}>
        {menuItems.map(navButton)}
      </List>

      {/* Footer nav */}
      <Box sx={{ px: 1.5, pb: 2 }}>
        <Divider sx={{ borderColor: alpha('#fff', 0.09), mb: 1 }} />
        <List disablePadding>
          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton onClick={() => go('/')} sx={{ borderRadius: 2.5, px: 1.5, py: 0.75, color: MUTED, '&:hover': { bgcolor: alpha('#fff', 0.07), color: '#fff' } }}>
              <ListItemIcon sx={{ color: 'inherit', minWidth: 38 }}><HomeIcon /></ListItemIcon>
              <ListItemText primary="Back to Home" primaryTypographyProps={{ fontSize: '0.88rem' }} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton onClick={() => go('/trips')} sx={{ borderRadius: 2.5, px: 1.5, py: 0.75, color: MUTED, '&:hover': { bgcolor: alpha('#fff', 0.07), color: '#fff' } }}>
              <ListItemIcon sx={{ color: 'inherit', minWidth: 38 }}><ExploreIcon /></ListItemIcon>
              <ListItemText primary="Browse Trips" primaryTypographyProps={{ fontSize: '0.88rem' }} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={handleLogout} sx={{ borderRadius: 2.5, px: 1.5, py: 0.75, color: alpha('#ff8a80', 0.9), '&:hover': { bgcolor: alpha('#ff5252', 0.12), color: '#ff8a80' } }}>
              <ListItemIcon sx={{ color: 'inherit', minWidth: 38 }}><LogoutIcon /></ListItemIcon>
              <ListItemText primary="Log out" primaryTypographyProps={{ fontSize: '0.88rem' }} />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Box>
  );

  // Don't render user dashboard until auth is verified
  if (!authReady) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: 'white',
          color: theme.palette.text.primary,
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            {menuItems.find(item => item.path === location.pathname)?.text || 'My Dashboard'}
          </Typography>

          <IconButton color="inherit" onClick={handleNotifOpen}>
            <Badge badgeContent={unreadNotifications} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          <IconButton
            onClick={handleProfileMenuOpen}
            sx={{ ml: 1 }}
          >
            <Avatar 
              src={user?.profile_picture_url ?? user?.profile_picture ?? user?.profilePicture}
              sx={{ 
                width: 36, 
                height: 36, 
                bgcolor: theme.palette.primary.main 
              }}
            >
              {user ? ((user.first_name || user.firstName || '').charAt(0) || (user.last_name || user.lastName || '').charAt(0) || (user.email || '?').charAt(0).toUpperCase()) : '?'}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notifAnchor}
        open={Boolean(notifAnchor)}
        onClose={handleNotifClose}
        PaperProps={{
          sx: { width: 320, mt: 1, maxHeight: 400 }
        }}
      >
        <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Notifications
          </Typography>
        </Box>
        <MenuItem onClick={handleNotifClose} disabled>
          <ListItemText secondary="No notifications" />
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleNotifClose}>
          <Typography variant="body2" color="primary" sx={{ width: '100%', textAlign: 'center' }}>
            View All Notifications
          </Typography>
        </MenuItem>
      </Menu>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          sx: { width: 220, mt: 1 }
        }}
      >
        <MenuItem onClick={() => { navigate('/user/profile'); handleProfileMenuClose(); }}>
          <ListItemIcon>
            <AccountCircle fontSize="small" />
          </ListItemIcon>
          <ListItemText>My Profile</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { navigate('/user/settings'); handleProfileMenuClose(); }}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>
            <Typography color="error">Logout</Typography>
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Sidebar Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: '#1c1206',
              border: 'none',
            },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: '#1c1206',
              border: 'none',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          bgcolor: theme.palette.background.default,
          minHeight: '100vh',
          mt: { xs: '56px', sm: '64px' }
        }}
      >
        <Container maxWidth="xl">
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
};

export default UserLayout;

